/**
 * Section 09f — LR_Cross Strategy.
 *
 * The rule lives in utils/strategies/lrCrossEngine.ts (byte-identical backend
 * twin that the scheduled scan runs). The alert wording lives in
 * backend/src/services/strategies/lrCrossText.ts. Every claim here about which
 * bar is marked, what an alert says, and that the scan and the chart agree is
 * asserted by `cd backend && npm run verify:lr-cross`.
 */
import React from 'react';
import { Typography, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import GuideCallout from '../GuideCallout';
import OpenInApp from '../OpenInApp';
import { GuideSection } from './types';

const H = ({ id, children }: { id: string; children: React.ReactNode }) => (
  <Typography id={`anchor-${id}`} variant="h5" sx={{ fontWeight: 700, mt: 4, mb: 1.25, scrollMarginTop: 80 }}>{children}</Typography>
);
const P = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body1" sx={{ mb: 1.25, lineHeight: 1.65, fontSize: '0.95rem' }}>{children}</Typography>
);

const MARKS: Array<[string, string]> = [
  ['▲ B', 'BUY: the first closed bar whose source close is above the top of its own LR body.'],
  ['▼ S', 'SHORT: the first closed bar whose source close is below the bottom of its own LR body.'],
  ['▲ B ✕S  /  ▼ S ✕L', 'The same cross, and it also exits the open opposite side. The alert title reads “BTCUSD: BUY ✕S”, and Dashboard → Recent Alerts shows ✕S beside the BUY chip.'],
  ['▲ B re  /  ▼ S re', 'A re-cross: price fell back into the body and crossed out the same way while already in. Still alerted.'],
];

const Section_strategy_lrcross: GuideSection = {
  id: 'strategy-lrcross',
  label: 'LR_Cross Strategy',
  group: 'strategies',
  icon: <CompareArrowsIcon fontSize="small" />,
  render: () => (
    <>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>LR_Cross Strategy</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        An alert when a closed candle — <strong>Regular or Heikin-Ashi</strong> — crosses the top or bottom of the
        <strong> LR body</strong>. You review each alert and act on it yourself.
      </Typography>

      <GuideCallout kind="warning" title="Alerts only — and not backtested">
        LR_Cross never places an order; the auto-trade switch on its schedules is ignored. Nothing about its returns
        has been measured. Look at the marks on your charts first.
      </GuideCallout>

      <H id="lrcross-body">The LR body</H>
      <P>
        The LR body is the linear-regression body the Charts <strong>LR</strong> chip draws over any candle type (the
        body of the CLN candle). Its <strong>top</strong> is the higher of the regression of the opens and the
        regression of the closes; its <strong>bottom</strong> is the lower. It is always built from the real candles,
        at <strong>your chart&apos;s LR length</strong> (Chart Settings → Candle). LR_Cross has no length of its own, so
        a mark always refers to a body you can see. At a long length such as 50 the body is thin — about a tenth of
        an average bar — so in practice the top and bottom are close together.
      </P>

      <H id="lrcross-rule">The rule</H>
      <P>
        <strong>BUY</strong> on the first closed bar whose source close is <em>above</em> the top of that bar&apos;s own
        body, when the previous bar&apos;s close was not above its own. <strong>SHORT</strong> is the mirror, below the
        bottom. A close exactly on the edge is not a cross. Only closed bars count, so a mark never appears or moves
        while a bar is still forming.
      </P>
      <P>
        <strong>Exits:</strong> an open long is exited by the next SHORT cross, and an open short by the next BUY. That
        is always the same bar, so there is one alert, not two. There is no stop and no target; set your own.
      </P>

      <H id="lrcross-source">Source candle</H>
      <P>
        The source decides <em>whose close</em> is compared with the body: <strong>Regular</strong> (the real close) or
        <strong> HA</strong> (the app&apos;s Heikin-Ashi close). HA alerts are roughly half as frequent and usually a bar
        later. There are two settings, on purpose: <strong>Chart Settings → LR_Cross</strong> for the chart marks, and
        the <strong>Source candle</strong> on the Strategies page (LR_Cross → Overview) for scans and alerts. Every
        price in an alert is the real close.
      </P>

      <H id="lrcross-alerts">Alerts and what to do with them</H>
      <P>
        On the Strategies page, select <strong>LR_Cross</strong> and add a scheduled scan (Strategy Scan tab) for a
        watchlist and timeframe, routed to the Dashboard and/or Telegram. Each new cross is one alert, titled e.g.
        <strong> BTCUSD: BUY ✕S</strong>. The message gives the source, the close, the body edge, the LR length, and —
        when the symbol has a listed option chain — an option idea: <strong>Bull Put (credit)</strong> for BUY,
        <strong> Bear Call (credit)</strong> for SHORT. Review it on the chart, then Execute, or set an NB/NS/BB/BS
        level (which can open a ⚡ option strategy).
      </P>

      <H id="lrcross-chart">What is drawn</H>
      <Table size="small" sx={{ mb: 2, '& td': { fontSize: '0.9rem', verticalAlign: 'top' } }}>
        <TableHead><TableRow><TableCell>Mark</TableCell><TableCell>Meaning</TableCell></TableRow></TableHead>
        <TableBody>
          {MARKS.map(([m, d]) => (
            <TableRow key={m}><TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{m}</TableCell><TableCell>{d}</TableCell></TableRow>
          ))}
        </TableBody>
      </Table>
      <P>
        Select the <strong>LR_Cross</strong> strategy chip on Charts and turn on the <strong>LR</strong> chip. If the
        LR chip is off, or the chart shows a different candle type from the marks&apos; source, the chart says so in a
        short notice — the marks are still right, but the crossing is not visible.
      </P>
      <P>
        <OpenInApp to="/charts/BTCUSD?interval=240&strategy=lr_cross&overlays=lrBody" label="Open BTCUSD 4H with LR_Cross on" />
      </P>
    </>
  ),
};

export default Section_strategy_lrcross;
